import React, { useState } from "react";
import {
  Shield, UserPlus, Repeat, Ban, ShieldAlert, Car, Package as IconoPaquete,
  Clock, Megaphone, Trees, Wrench, PartyPopper, Siren, DoorOpen, BarChart3,
  ScrollText, BookUser, Search, FileText, Users, Briefcase, Truck,
} from "lucide-react";
import { catalogos as apiCatalogos, puntosAcceso as apiPuntos } from "../../api/modulos.service";
import CentroSeguridad from "./CentroSeguridad";
import LlegadaSinAviso from "./LlegadaSinAviso";
import Recurrentes from "./Recurrentes";
import Restricciones from "./Restricciones";
import Incidentes from "./Incidentes";
import Vehiculos from "./Vehiculos";
import Paquetes from "./Paquetes";
import Comunicados from "./Comunicados";
import Amenidades from "./Amenidades";
import Mantenimiento from "./Mantenimiento";
import Eventos from "./Eventos";
import Sos from "./Sos";
import Historial from "./Historial";
import Busqueda from "./Busqueda";
import Encuestas from "./Encuestas";
import Documentos from "./Documentos";
import Junta from "./Junta";
import Proveedores from "./Proveedores";
import Entregas from "./Entregas";
import { PuntosAcceso, Turnos, Guardias } from "./Administracion";
import { PanelGeneral, Estadisticas, Bitacora, Directorio } from "./PanelAdmin";
import { Vacio, Aviso } from "./base";

/**
 * EL NAVEGADOR DE LOS MÓDULOS.
 *
 * Una sola pieza que se monta en los tres paneles y decide qué pestañas
 * enseñar **según el rol y según lo que la residencial tenga encendido**. La
 * alternativa —que cada panel arme su propia lista— garantiza que al mes
 * tengan tres menús distintos para las mismas funciones.
 *
 * Los catálogos y los puntos de acceso se cargan UNA vez aquí y se pasan hacia
 * abajo: son los mismos para todas las pantallas y pedirlos en cada una
 * multiplicaría las llamadas sin ganar nada.
 */

const PESTANAS = [
  { id: "seguridad", etiqueta: "Ahora", icono: Shield, roles: ["guard", "admin"], componente: CentroSeguridad },
  { id: "llegada", etiqueta: "Llegó alguien", icono: UserPlus, roles: ["guard", "admin"], componente: LlegadaSinAviso },
  { id: "recurrentes", etiqueta: "Recurrentes", icono: Repeat, roles: ["guard", "resident", "admin"], componente: Recurrentes },
  { id: "restricciones", etiqueta: "Restricciones", icono: Ban, roles: ["guard", "admin"], componente: Restricciones },
  { id: "incidentes", etiqueta: "Incidentes", icono: ShieldAlert, roles: ["guard", "admin"], componente: Incidentes },
  { id: "vehiculos", etiqueta: "Vehículos", icono: Car, roles: ["guard", "resident", "admin"], componente: Vehiculos },
  { id: "paquetes", etiqueta: "Paquetería", icono: IconoPaquete, roles: ["guard", "resident", "admin"], componente: Paquetes, modulo: "paquetes" },
  { id: "eventos", etiqueta: "Eventos", icono: PartyPopper, roles: ["guard", "resident", "admin"], componente: Eventos },
  { id: "turnos", etiqueta: "Turnos", icono: Clock, roles: ["guard", "admin"], componente: Turnos },
  { id: "comunicados", etiqueta: "Comunicados", icono: Megaphone, roles: ["guard", "resident", "admin"], componente: Comunicados },
  { id: "amenidades", etiqueta: "Amenidades", icono: Trees, roles: ["resident", "admin"], componente: Amenidades, modulo: "amenidades" },
  { id: "mantenimiento", etiqueta: "Mantenimiento", icono: Wrench, roles: ["resident", "admin"], componente: Mantenimiento, modulo: "mantenimiento" },
  { id: "sos", etiqueta: "Emergencia", icono: Siren, roles: ["resident", "guard", "admin"], componente: Sos, modulo: "sos" },
  { id: "buscar", etiqueta: "Buscar", icono: Search, roles: ["guard", "admin"], componente: Busqueda },
  { id: "historial", etiqueta: "Historial", icono: ScrollText, roles: ["guard", "admin"], componente: Historial },
  { id: "entregas", etiqueta: "Entregas", icono: Truck, roles: ["guard", "resident", "admin"], componente: Entregas },
  { id: "encuestas", etiqueta: "Encuestas", icono: BarChart3, roles: ["resident", "admin"], componente: Encuestas },
  { id: "documentos", etiqueta: "Documentos", icono: FileText, roles: ["guard", "resident", "admin"], componente: Documentos },
  { id: "junta", etiqueta: "Junta directiva", icono: Users, roles: ["resident", "admin"], componente: Junta },
  { id: "proveedores", etiqueta: "Proveedores", icono: Briefcase, roles: ["guard", "resident", "admin"], componente: Proveedores },
  { id: "puntos", etiqueta: "Puntos de acceso", icono: DoorOpen, roles: ["admin"], componente: PuntosAcceso },
  { id: "guardias", etiqueta: "Guardias", icono: Shield, roles: ["admin"], componente: Guardias },
  { id: "panel", etiqueta: "Panel", icono: BarChart3, roles: ["admin"], componente: PanelGeneral },
  { id: "estadisticas", etiqueta: "Estadísticas", icono: BarChart3, roles: ["admin"], componente: Estadisticas },
  { id: "bitacora", etiqueta: "Bitácora", icono: ScrollText, roles: ["admin"], componente: Bitacora },
  { id: "directorio", etiqueta: "Directorio", icono: BookUser, roles: ["admin"], componente: Directorio },
];

const estilos = {
  /* `minmax(0, 1fr)` y no la columna implícita `auto`.
   *
   * Aquí se monta CADA una de las pestañas, así que esta rejilla es la celda
   * que las contiene a todas. Una pista `auto` tiene suelo en el contenido de
   * su hijo, de modo que cualquier pantalla que por dentro pida más ancho del
   * que hay —una rejilla de contadores que bajo medida intrínseca reclama
   * 4×180 px, un botón con `white-space: nowrap` y una etiqueta larga— se
   * llevaba la página entera y aparecía una barra horizontal.
   *
   * Medido en «Ahora», la primera pantalla del guardia: 804 px de contenido
   * en una ventana de 390. Con `minmax(0, 1fr)` los hijos pueden encoger y
   * cada rejilla interna recalcula sus columnas para el ancho real. */
  contenedor: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "var(--space-5)",
  },
  /* Las pestañas se desplazan en horizontal en el teléfono en vez de
     apilarse: apiladas, ocuparían media pantalla antes de empezar a leer. */
  barra: {
    display: "flex",
    gap: "var(--space-2)",
    overflowX: "auto",
    paddingBottom: "var(--space-2)",
    WebkitOverflowScrolling: "touch",
  },
  pestana: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    flexShrink: 0,
    minHeight: "44px",
    padding: "var(--space-2) var(--space-4)",
    borderRadius: "999px",
    border: "1px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    color: "var(--text-secondary)",
    fontSize: "var(--text-sm)",
    fontWeight: 500,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  activa: {
    borderColor: "var(--gold)",
    backgroundColor: "var(--gold)",
    color: "#15171b",
    fontWeight: 600,
  },
};

export default function Navegador({ rol, inicial }) {
  const [activa, setActiva] = useState(inicial || null);
  const [catalogos, setCatalogos] = useState(null);
  /**
   * `cargando` · `listo` · `fallo` — tres estados, no dos.
   *
   * Antes se guardaba `null` tanto mientras cargaba como cuando fallaba, y la
   * condición de abajo se escribía `catalogos?.modulos && …=== false`. Con el
   * catálogo sin cargar, esa condición se cortocircuitaba y **se mostraban
   * TODAS las pestañas, incluidas las de los módulos que la residencial tiene
   * apagados** — y como los módulos no tienen candado en el servidor, esas
   * pantallas además funcionaban. Un interruptor que falla abriendo.
   *
   * Ahora, sin catálogo confirmado no se muestra ningún módulo opcional.
   */
  const [estadoCatalogo, setEstadoCatalogo] = useState("cargando");
  const [puntos, setPuntos] = useState([]);

  React.useEffect(() => {
    apiCatalogos()
      .then((c) => {
        setCatalogos(c);
        setEstadoCatalogo("listo");
      })
      .catch(() => {
        setCatalogos(null);
        setEstadoCatalogo("fallo");
      });
    apiPuntos.listar({}).then((r) => setPuntos(r.accessPoints || [])).catch(() => setPuntos([]));
  }, []);

  const visibles = PESTANAS.filter((p) => {
    if (!p.roles.includes(rol)) return false;
    if (p.modulo) {
      // Un módulo apagado no se enseña. La pantalla lo explicaría igual, pero
      // una pestaña que sólo sirve para decir «esto está apagado» es ruido.
      // Y si no sabemos si está encendido, tampoco: ante la duda, cerrado.
      if (estadoCatalogo !== "listo") return false;
      if (catalogos?.modulos?.[p.modulo] === false) return false;
    }
    return true;
  });

  const elegida = visibles.find((p) => p.id === activa) || visibles[0];
  const Componente = elegida?.componente;

  /* Mientras no se sepa qué módulos están encendidos no se dibuja la barra.
     Dibujarla y corregirla después haría dos cosas feas: enseñar un instante
     las pestañas de módulos apagados, y sacar al usuario de la pestaña en la
     que estaba para devolverlo medio segundo más tarde. */
  if (estadoCatalogo === "cargando") {
    return (
      <div style={estilos.contenedor}>
        <Vacio>Cargando…</Vacio>
      </div>
    );
  }

  if (!Componente) return null;

  return (
    <div style={estilos.contenedor}>
      {/* Si el catálogo no cargó faltan pestañas, y hay que decirlo: una
          barra más corta sin explicación se lee como «esto no existe». */}
      {estadoCatalogo === "fallo" && (
        <Aviso>
          No se pudo cargar la configuración de los módulos, así que algunas
          pestañas no se están mostrando. Recargue la página para reintentar.
        </Aviso>
      )}
      <div style={estilos.barra} role="tablist">
        {visibles.map((p) => {
          const Icono = p.icono;
          const esActiva = p.id === elegida.id;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={esActiva}
              onClick={() => setActiva(p.id)}
              style={{ ...estilos.pestana, ...(esActiva ? estilos.activa : {}) }}
            >
              <Icono size={16} />
              {p.etiqueta}
            </button>
          );
        })}
      </div>

      <Componente
        rol={rol}
        catalogos={catalogos}
        modulos={catalogos?.modulos}
        puntosAcceso={puntos}
      />
    </div>
  );
}
